<template>
  <v-container class="breaking-event-details-container">
    <Loader v-if="!heading" color="white" text=""></Loader>
    <h2 fluid class="pa-2 breaking-event-container" v-if="heading">
      {{ heading }}
      <v-icon
        v-if="showCopyBtn"
        @click="handleCopyClick"
        class="pull-right"
        size="20"
        color="white"
        title="Copy this breaking event"
      >
        fas fa-clipboard
      </v-icon>
    </h2>
    <div
      v-if="details.feature"
      class="pa-2 breaking-event-container breaking-event-details"
    >
      <p>
        <span class="breaking-event-title application-heading">{{
          title
        }}</span>
      </p>
      <div v-if="countries && countries.length">
        <DetailsListItemLocations
          :countries="countries"
          theme="dark"
          @after-click="handleClose"
          class="breaking-event-text countries"
        ></DetailsListItemLocations>
      </div>
      <div v-if="location">
        <span class="breaking-event-label application-heading">Lat</span>:
        <span class="breaking-event-text">{{ location.lat }}</span>
        <span class="ml-2 breaking-event-label application-heading">Lon</span>:
        <span class="breaking-event-text">{{ location.lon }}</span>
      </div>
      <div v-if="source">
        <span class="mr-1 breaking-event-label application-heading"
          >Source:</span
        >
        <a
          :href="link"
          class="breaking-event-text"
          v-if="link && source"
          target="_blank"
          >{{ source }}
        </a>
      </div>

      <div v-if="publishedDateTime">
        <span class="mr-1 breaking-event-label application-heading"
          >Published Date:</span
        >
        <span class="breaking-event-text">{{ publishedDateTime }}</span>
      </div>
      <div v-if="revisedDateTime">
        <span class="mr-1 breaking-event-label application-heading"
          >Revised Date:</span
        >
        <span class="breaking-event-text">{{ revisedDateTime }}</span>
      </div>
      <div v-if="eventTime" class="mb-5">
        <span class="mr-1 breaking-event-label application-heading"
          >Event Date:</span
        >
        <span class="breaking-event-text">{{ eventTime }}</span>
      </div>
      <div class="breaking-event-body" ref="container">
        <span v-html="body" class="html-body"></span>
      </div>
    </div>
    <v-overlay :absolute="true" :value="isScreenShot">
      <div class="copy-overlay-indicator">
        <v-icon size="50" color="white">fas fa-check</v-icon>
        <div><strong>Copied</strong></div>
      </div>
    </v-overlay>
  </v-container>
</template>

<script>
import { hasCopyAccess } from "@/services/user/user.service";
import { mapGetters } from "vuex";
import { MODULE_PREFIX_ANALYSIS, MODULE_PREFIX_BREAKING_EVENTS } from "@/store";
import { dataToCopy } from "@/services/search/wod.service";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import lodashGet from "lodash/get";
import { getFormatted, getFormattedDate } from "@/common/date.utilities";
import lodashDebounce from "lodash/debounce";
import { embedInfoText } from "@/common/basic.utilities";
import {
  GET_ANALYSIS_DETAILS,
  GET_BREAKING_EVENTS_DETAILS,
} from "@/store/actions.type";

const EMITTED_ACTIONS = {
  CLOSE: "close",
};

export default {
  name: "BreakingEventsDetails",
  data: () => ({
    showCopyBtn: hasCopyAccess(),
    isScreenShot: false,
  }),
  components: {
    Loader: () => import("@/components/common/Loader"),
    DetailsListItemLocations: () =>
      import("@/components/common/details/DetailsListItemLocations"),
  },
  computed: {
    ...mapGetters({
      details: MODULE_PREFIX_BREAKING_EVENTS + "details",
    }),
    heading() {
      if (lodashGet(this.details, "title")) {
        return this.details.title.toUpperCase();
      }
      return null;
    },
    body() {
      if (lodashGet(this.details, "feature.properties.formatted.body")) {
        return this.details.feature.properties.formatted.body;
      }
      return null;
    },
    title() {
      if (lodashGet(this.details, "feature.properties.formatted.title")) {
        return this.details.feature.properties.formatted.title;
      }
      return null;
    },
    countries() {
      if (
        lodashGet(
          this.details,
          "feature.properties.formatted.countriesDisplayMetadata"
        )
      ) {
        return this.details.feature.properties.formatted
          .countriesDisplayMetadata;
      }
      return null;
    },
    eventTime() {
      if (lodashGet(this.details, "feature.properties.formatted.occurred")) {
        return getFormattedDate(
          this.details.feature.properties.formatted.occurred
        );
      }
      return null;
    },
    location() {
      if (lodashGet(this.details, "feature.properties.formatted.location")) {
        return this.details.feature.properties.formatted.location;
      }
      return null;
    },
    source() {
      if (lodashGet(this.details, "feature.properties.formatted.source")) {
        return this.details.feature.properties.formatted.source;
      }
      return null;
    },
    link() {
      if (lodashGet(this.details, "feature.properties.formatted.link")) {
        return this.details.feature.properties.formatted.link;
      }
      return null;
    },
    publishedDateTime() {
      if (
        lodashGet(
          this.details,
          "feature.properties.formatted.initialPublishedDate"
        )
      ) {
        return getFormatted(
          this.details.feature.properties.formatted.initialPublishedDate
        );
      }
      return null;
    },
    revisedDateTime() {
      if (
        lodashGet(
          this.details,
          "feature.properties.formatted.revisedPublishedDate"
        )
      ) {
        return getFormatted(
          this.details.feature.properties.formatted.revisedPublishedDate
        );
      }
      return null;
    },
  },
  methods: {
    handleClickImg(evt) {
      evt.preventDefault();
      const src = evt.currentTarget.getAttribute("src");
      const newTab = window.open();
      newTab.document.body.innerHTML = `<img src="${src}">`;
    },
    async handleCopyClick() {
      document.activeElement.blur();
      const colsToCopy = ["title", "body", "location", "occurred"];
      let item = {};
      item.title = this.title;
      item.countries = this.countries;
      item.body = this.body;
      item.link = this.link;
      item.occurred = this.eventTime;
      item.location = `${this.location.lat}, ${this.location.lon}`;

      if (item.countries) {
        colsToCopy.push("countries");
      }

      if (item.source) {
        colsToCopy.push("source");
      }

      if (item.link) {
        colsToCopy.push("link");
      }

      this.isScreenShot = true;

      await dataToCopy(item, colsToCopy, true);

      setTimeout(() => {
        this.isScreenShot = false;
      }, 500);

      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.breaking_events,
        EVENT_ACTIONS.clicked,
        "Copy To Clipboard"
      );
    },
    handleClose() {
      this.$emit(EMITTED_ACTIONS.CLOSE);
    },
  },
  watch: {
    details: {
      handler() {
        lodashDebounce(() => {
          if (lodashGet(this.$refs, "container")) {
            this.$refs.container.querySelectorAll("img").forEach((input) => {
              input.style.width = "300px";
              input.className = "cursor-pointer";
              input.addEventListener("click", this.handleClickImg);
            });

            // const htmlBody = this.$refs.container.querySelector(".html-body");
            // embedInfoText(htmlBody, "Show source info", this.source);
          }
        }, 500)();
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.$store.dispatch(
      MODULE_PREFIX_BREAKING_EVENTS + GET_BREAKING_EVENTS_DETAILS,
      {
        title: null,
        feature: null,
      }
    );
    if (lodashGet(this.$refs, "container")) {
      this.$refs.container.querySelectorAll("img").forEach((input) => {
        input.removeEventListener("click", this.handleClickImg);
      });
    }
  },
};
</script>

<style scoped>
.breaking-event-details-container {
  height: calc(100vh - 30px);
}
.breaking-event-container {
  background-color: #191a1a !important;
  color: white;
}
.breaking-event-details {
  display: flex;
  flex-direction: column;
  height: calc(100% - 3em); /* 3em is height of title */
}
.breaking-event-body {
  font-size: 12px;
  overflow-y: auto;
  padding-right: 15px;
}
.breaking-event-text {
  font-size: 12px;
}
.breaking-event-label {
  font-size: 12px;
}
.breaking-event-text.countries {
  display: inline-block;
}
/* These are for the scroll bar in the content container */
::-webkit-scrollbar {
  width: 8px;
  height: 3px;
}
.dark::-webkit-scrollbar-track {
  background-color: red; /* #646464; */
  border-radius: 3px;
}
.dark::-webkit-scrollbar-track-piece {
  background-color: #000;
}
.dark::-webkit-scrollbar-thumb {
  height: 50px;
  background-color: #666;
  border-radius: 3px;
}
.dark::-webkit-scrollbar-thumb:hover {
  background: black;
}

.v-overlay {
  left: 0px !important;
  background-color: rgba(0, 0, 0, 0) !important;
}

.v-overlay--active.v-overlay {
  background-color: rgba(0, 0, 0, 0.75) !important;
}

.copy-overlay-indicator {
  width: 120px;
  height: 120px;
  background-color: green;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  font-size: large;
}
</style>
